'use client'

import { useState, useEffect } from 'react'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { getRole, getPermissionsMap } from '@/lib/api/roles/roles-api'
import {
    Role,
    RolePermissions,
    PermissionsMap,
} from '@/lib/api/common/interfaces/role.interface'
import { toast } from 'react-toastify'
import { useNavigate, useParams } from 'react-router-dom'
import {
    MdArrowBack,
    MdShield,
    MdEdit,
    MdPeople,
    MdStar,
    MdCheck,
    MdClose,
    MdExpandMore,
    MdExpandLess,
    MdPerson,
    MdBusiness,
} from 'react-icons/md'
import { getAccessibleCompanies } from '@/lib/api/organization/organization-api'
import { AccessibleCompany } from '@/lib/api/common/interfaces/user.interface'

const ACTION_LABELS: Record<string, string> = {
    read: 'Visualizar',
    create: 'Criar',
    update: 'Editar',
    delete: 'Excluir',
}

const ACTIONS = ['read', 'create', 'update', 'delete']

const USER_TYPE_LABELS: Record<string, string> = {
    PROFESSIONAL: 'Profissional',
    EMPLOYEE: 'Funcionário',
    COMPANY: 'Empresa',
    CUSTOM: 'Personalizado',
    MASTER: 'Master',
}

interface RoleDetail extends Role {
    users?: Array<{
        id: number
        name: string
        email: string
        userType: string
        picture: string | null
    }>
}

export default function RoleDetailPage() {
    const navigate = useNavigate()
    const { id } = useParams<{ id: string }>()
    const { canUpdate } = useResourcePermissions('security', 'permissions')

    const [role, setRole] = useState<RoleDetail | null>(null)
    const [permissionsMap, setPermissionsMap] = useState<PermissionsMap>({})
    const [loading, setLoading] = useState(true)
    const [loadingMap, setLoadingMap] = useState(true)
    const [expandedModules, setExpandedModules] = useState<
        Record<string, boolean>
    >({})
    const [parsedPermissions, setParsedPermissions] = useState<
        Record<string, Record<string, string[]>>
    >({})
    const [isFullAccess, setIsFullAccess] = useState(false)
    const [companies, setCompanies] = useState<AccessibleCompany[]>([])

    useEffect(() => {
        if (id) {
            loadRole()
            loadPermissionsMap()
            loadCompanies()
        }
    }, [id])

    useEffect(() => {
        if (
            role?.permissions &&
            Object.keys(permissionsMap).length > 0
        ) {
            parsePermissions(role.permissions)
        }
    }, [role, permissionsMap])

    const loadRole = async () => {
        if (!id) return
        setLoading(true)
        try {
            const response = await getRole(parseInt(id, 10))
            if (response.success) {
                const result = Array.isArray(response.result)
                    ? response.result[0]
                    : response.result
                if (result) {
                    setRole(result as RoleDetail)
                } else {
                    toast.error('Perfil de acesso não encontrado')
                    navigate('/security/permissions')
                }
            } else {
                toast.error(
                    response.error || 'Erro ao carregar perfil de acesso'
                )
                navigate('/security/permissions')
            }
        } catch (error) {
            toast.error('Erro ao carregar perfil de acesso')
            navigate('/security/permissions')
        } finally {
            setLoading(false)
        }
    }

    const loadPermissionsMap = async () => {
        setLoadingMap(true)
        try {
            const response = await getPermissionsMap()
            if (response.success) {
                setPermissionsMap(response.result as PermissionsMap)
            }
        } catch (error) {
            console.error('Erro ao carregar mapa de permissões:', error)
        } finally {
            setLoadingMap(false)
        }
    }

    const loadCompanies = async () => {
        try {
            const response = await getAccessibleCompanies()
            if (response.success && Array.isArray(response.result)) {
                setCompanies(response.result)
            }
        } catch (error) {
            console.error('Erro ao carregar unidades:', error)
        }
    }

    const parsePermissions = (rolePerms: RolePermissions) => {
        if (!rolePerms || typeof rolePerms !== 'object') return

        const perms = rolePerms as Record<string, any>
        if (perms['*'] === '*') {
            setIsFullAccess(true)
            // Preencher todas as permissões
            const allPerms: Record<string, Record<string, string[]>> = {}
            for (const [moduleKey, moduleData] of Object.entries(
                permissionsMap
            )) {
                allPerms[moduleKey] = {}
                for (const resourceKey of Object.keys(moduleData.resources)) {
                    allPerms[moduleKey][resourceKey] = [...ACTIONS]
                }
            }
            setParsedPermissions(allPerms)
            return
        }

        setIsFullAccess(false)
        const parsed: Record<string, Record<string, string[]>> = {}

        for (const [moduleKey, moduleValue] of Object.entries(perms)) {
            if (moduleKey === '*') continue
            if (moduleValue === '*') {
                parsed[moduleKey] = {}
                const moduleMap = permissionsMap[moduleKey]
                if (moduleMap) {
                    for (const resourceKey of Object.keys(
                        moduleMap.resources
                    )) {
                        parsed[moduleKey][resourceKey] = [...ACTIONS]
                    }
                }
            } else if (typeof moduleValue === 'object') {
                parsed[moduleKey] = {}
                for (const [resourceKey, resourceValue] of Object.entries(
                    moduleValue as Record<string, any>
                )) {
                    if (resourceValue === '*') {
                        parsed[moduleKey][resourceKey] = [...ACTIONS]
                    } else if (Array.isArray(resourceValue)) {
                        parsed[moduleKey][resourceKey] = [...resourceValue]
                    }
                }
            }
        }

        setParsedPermissions(parsed)
    }

    const toggleModule = (moduleKey: string) => {
        setExpandedModules(prev => ({
            ...prev,
            [moduleKey]: !prev[moduleKey],
        }))
    }

    const hasAction = (
        moduleKey: string,
        resourceKey: string,
        action: string
    ): boolean => {
        return (
            parsedPermissions[moduleKey]?.[resourceKey]?.includes(action) ??
            false
        )
    }

    const getModuleState = (
        moduleKey: string
    ): 'none' | 'partial' | 'all' => {
        const moduleMap = permissionsMap[moduleKey]
        if (!moduleMap) return 'none'

        const resourceKeys = Object.keys(moduleMap.resources)
        const totalPossible = resourceKeys.length * ACTIONS.length
        let totalChecked = 0

        for (const resourceKey of resourceKeys) {
            for (const action of ACTIONS) {
                if (hasAction(moduleKey, resourceKey, action)) {
                    totalChecked++
                }
            }
        }

        if (totalChecked === 0) return 'none'
        if (totalChecked === totalPossible) return 'all'
        return 'partial'
    }

    const countActiveModules = (): number => {
        let count = 0
        for (const moduleKey of Object.keys(permissionsMap)) {
            if (getModuleState(moduleKey) !== 'none') {
                count++
            }
        }
        return count
    }

    const countTotalPermissions = (): number => {
        let count = 0
        for (const resources of Object.values(parsedPermissions)) {
            for (const actions of Object.values(resources)) {
                count += actions.length
            }
        }
        return count
    }

    const formatDate = (dateStr: string) => {
        return new Date(dateStr).toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        })
    }

    if (loading || loadingMap) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando perfil de acesso...
                </p>
            </div>
        )
    }

    if (!role) return null

    const activeModules = countActiveModules()
    const totalPermissions = countTotalPermissions()

    return (
        <div className="flex flex-col gap-6 animate-fade-in">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate('/security/permissions')}
                        className="btn btn-ghost btn-circle btn-sm"
                    >
                        <MdArrowBack className="text-xl" />
                    </button>
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-neutral to-neutral/70 flex items-center justify-center shadow-lg">
                        <MdShield className="text-2xl text-white" />
                    </div>
                    <div>
                        <div className="flex items-center gap-2">
                            <h1 className="text-2xl font-bold text-base-content">
                                {role.name}
                            </h1>
                            {role.isSystem && (
                                <span className="badge badge-ghost badge-sm">
                                    Sistema
                                </span>
                            )}
                        </div>
                        {role.description && (
                            <p className="text-sm text-base-content/60">
                                {role.description}
                            </p>
                        )}
                    </div>
                </div>
                {canUpdate && !role.isSystem && (
                    <button
                        className="btn btn-secondary gap-2"
                        onClick={() =>
                            navigate(
                                `/security/permissions/edit/${role.id}`
                            )
                        }
                    >
                        <MdEdit className="text-lg" />
                        Editar
                    </button>
                )}
            </div>

            {/* Informações gerais */}
            <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                <h2 className="text-lg font-semibold text-base-content mb-4">
                    Informações Gerais
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="stat bg-base-200/30 rounded-lg p-4">
                        <div className="stat-title text-xs">Tipo</div>
                        <div className="stat-value text-lg">
                            {role.isSystem ? 'Sistema' : 'Customizado'}
                        </div>
                    </div>
                    {role.defaultForType && (
                        <div className="stat bg-secondary/10 rounded-lg p-4">
                            <div className="stat-title text-xs flex items-center gap-1">
                                <MdStar className="text-secondary" />
                                Padrão para
                            </div>
                            <div className="stat-value text-lg text-secondary">
                                {USER_TYPE_LABELS[role.defaultForType] ||
                                    role.defaultForType}
                            </div>
                        </div>
                    )}
                    <div className="stat bg-base-200/30 rounded-lg p-4">
                        <div className="stat-title text-xs flex items-center gap-1">
                            <MdShield className="text-xs" />
                            Permissões
                        </div>
                        <div className="stat-value text-lg">
                            {isFullAccess
                                ? 'Acesso total'
                                : `${totalPermissions} ações`}
                        </div>
                        <div className="stat-desc">
                            {isFullAccess
                                ? 'Todos os módulos'
                                : `em ${activeModules} ${activeModules === 1 ? 'módulo' : 'módulos'}`}
                        </div>
                    </div>
                    <div className="stat bg-base-200/30 rounded-lg p-4">
                        <div className="stat-title text-xs flex items-center gap-1">
                            <MdPeople className="text-xs" />
                            Usuários
                        </div>
                        <div className="stat-value text-lg">
                            {role.usersCount ?? role.users?.length ?? 0}
                        </div>
                        <div className="stat-desc">vinculados a este perfil</div>
                    </div>
                </div>
                <div className="mt-4 flex gap-4 text-xs text-base-content/50">
                    <span>Criado em {formatDate(role.createdAt)}</span>
                    <span>Atualizado em {formatDate(role.updatedAt)}</span>
                </div>
            </div>

            {/* Matriz de permissões */}
            <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                <h2 className="text-lg font-semibold text-base-content mb-2">
                    Permissões
                </h2>
                <p className="text-sm text-base-content/60 mb-6">
                    {isFullAccess
                        ? 'Este perfil possui acesso total a todos os módulos e recursos do sistema.'
                        : 'Detalhamento dos módulos e ações permitidos para este perfil.'}
                </p>

                <div className="flex flex-col gap-3">
                    {Object.entries(permissionsMap).map(
                        ([moduleKey, moduleData]) => {
                            const moduleState = getModuleState(moduleKey)
                            const isExpanded =
                                expandedModules[moduleKey] ?? false
                            const resourceEntries = Object.entries(
                                moduleData.resources
                            )

                            return (
                                <div
                                    key={moduleKey}
                                    className="border border-base-300/50 rounded-lg overflow-hidden"
                                >
                                    <button
                                        type="button"
                                        className="w-full flex items-center gap-3 p-3 bg-base-200/30 hover:bg-base-200/50 transition-colors cursor-pointer"
                                        onClick={() =>
                                            toggleModule(moduleKey)
                                        }
                                    >
                                        <div
                                            className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                                                moduleState === 'all'
                                                    ? 'bg-success'
                                                    : moduleState === 'partial'
                                                      ? 'bg-warning'
                                                      : 'bg-base-300'
                                            }`}
                                        />
                                        <span className="flex-1 text-left font-semibold text-sm text-base-content">
                                            {moduleData.label}
                                        </span>
                                        <div className="flex items-center gap-2">
                                            <span
                                                className={`badge badge-sm ${
                                                    moduleState === 'all'
                                                        ? 'badge-success'
                                                        : moduleState ===
                                                            'partial'
                                                          ? 'badge-warning'
                                                          : 'badge-ghost'
                                                }`}
                                            >
                                                {moduleState === 'all'
                                                    ? 'Acesso total'
                                                    : moduleState === 'partial'
                                                      ? 'Parcial'
                                                      : 'Sem acesso'}
                                            </span>
                                            {isExpanded ? (
                                                <MdExpandLess className="text-xl text-base-content/50" />
                                            ) : (
                                                <MdExpandMore className="text-xl text-base-content/50" />
                                            )}
                                        </div>
                                    </button>

                                    {isExpanded && (
                                        <div className="p-3">
                                            <div className="overflow-x-auto">
                                                <table className="table table-sm w-full">
                                                    <thead>
                                                        <tr>
                                                            <th className="text-xs font-medium text-base-content/50 w-2/5">
                                                                Recurso
                                                            </th>
                                                            {ACTIONS.map(
                                                                action => (
                                                                    <th
                                                                        key={
                                                                            action
                                                                        }
                                                                        className="text-center text-xs font-medium text-base-content/50 w-[15%]"
                                                                    >
                                                                        {
                                                                            ACTION_LABELS[
                                                                                action
                                                                            ]
                                                                        }
                                                                    </th>
                                                                )
                                                            )}
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {resourceEntries.map(
                                                            ([
                                                                resourceKey,
                                                                resourceData,
                                                            ]) => (
                                                                <tr
                                                                    key={
                                                                        resourceKey
                                                                    }
                                                                >
                                                                    <td className="text-sm text-base-content">
                                                                        {
                                                                            resourceData.label
                                                                        }
                                                                    </td>
                                                                    {ACTIONS.map(
                                                                        action => {
                                                                            const allowed =
                                                                                hasAction(
                                                                                    moduleKey,
                                                                                    resourceKey,
                                                                                    action
                                                                                )
                                                                            return (
                                                                                <td
                                                                                    key={
                                                                                        action
                                                                                    }
                                                                                    className="text-center"
                                                                                >
                                                                                    {allowed ? (
                                                                                        <MdCheck className="text-lg text-success mx-auto" />
                                                                                    ) : (
                                                                                        <MdClose className="text-lg text-base-content/20 mx-auto" />
                                                                                    )}
                                                                                </td>
                                                                            )
                                                                        }
                                                                    )}
                                                                </tr>
                                                            )
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )
                        }
                    )}
                </div>
            </div>

            {/* Usuários vinculados */}
            {role.users && role.users.length > 0 && (
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h2 className="text-lg font-semibold text-base-content mb-4">
                        Usuários Vinculados
                    </h2>
                    <div className="flex flex-col gap-2">
                        {role.users.map(u => (
                            <div
                                key={u.id}
                                className="flex items-center gap-3 p-3 rounded-lg hover:bg-base-200/30 transition-colors"
                            >
                                {u.picture ? (
                                    <img
                                        src={u.picture}
                                        alt={u.name}
                                        className="w-9 h-9 rounded-full object-cover"
                                    />
                                ) : (
                                    <div className="w-9 h-9 rounded-full bg-base-200 flex items-center justify-center">
                                        <MdPerson className="text-lg text-base-content/40" />
                                    </div>
                                )}
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-base-content truncate">
                                        {u.name}
                                    </p>
                                    <p className="text-xs text-base-content/50 truncate">
                                        {u.email}
                                    </p>
                                </div>
                                <span className="badge badge-ghost badge-sm flex-shrink-0">
                                    {USER_TYPE_LABELS[u.userType] ||
                                        u.userType}
                                </span>
                            </div>
                        ))}
                        {(role.usersCount ?? 0) > (role.users?.length ?? 0) && (
                            <p className="text-xs text-base-content/50 text-center pt-2">
                                Exibindo {role.users.length} de{' '}
                                {role.usersCount} usuários
                            </p>
                        )}
                    </div>
                </div>
            )}
        </div>
    )
}
